import React, { ReactNode, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalSize = 'md' | 'lg' | 'xl';
interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
}
const SIZES: Record<ModalSize, string> = {
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl'
};
export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  size = 'md'
}: ModalProps) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-1144" data-mp="2k6zyqmzau8u3">
      {open && <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4 backdrop-blur-sm" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onMouseDown={onClose} data-id="element-1145" data-mp="2f6bzxf4twnpb">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="modal-title" className={`flex max-h-[92vh] w-full flex-col rounded-2xl bg-white shadow-lift ${SIZES[size]}`} initial={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} onMouseDown={e => e.stopPropagation()} data-id="element-1146" data-mp="2n20rrnwhqe2d">
            <div className="flex shrink-0 items-start justify-between gap-4 px-6 pt-6" data-id="element-1147" data-mp="1pd7ajzyfvg0x">
              <div data-id="element-1148" data-mp="1uc0jdf2s6iur">
                <h2 id="modal-title" className="text-lg font-semibold text-navy" data-id="element-1149" data-mp="1gelss70sk517">
                  {title}
                </h2>
                {description && <p className="mt-1 text-sm text-muted" data-id="element-1150" data-mp="1kenm9swdhs5e">{description}</p>}
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-muted transition hover:bg-slate-100 hover:text-navy" data-id="element-1151" data-mp="3n3npmq4g0mdu">
                <XIcon className="h-5 w-5" data-id="element-1152" data-mp="3dcjey2lsvnot" />
              </button>
            </div>
            <div className="min-h-0 overflow-y-auto px-6 py-5" data-id="element-1153" data-mp="3dagc2hzju02o">{children}</div>
            {footer && <div className="flex shrink-0 justify-end gap-2 rounded-b-2xl border-t border-slate-100 bg-canvas/60 px-6 py-4" data-id="element-1154" data-mp="38bn22zjh0r79">{footer}</div>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}